import { ButtonDynamic, ButtonPrimary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useEffect, useMemo, useState } from "react"
import { useAsyncCallback } from "@nova/use-async-callback"
import { GenericUpload, ImageProcessingState, JobStatusType, SidePanelLayer } from "@nova/constants"
import EventEmitter from "events"
import { useUploadFlowContext } from "panels/content/side-panel/upload-flow/useUploadFlowContext"
import { InspectorPanelMetadata } from "features/map/inspector-panel/FlightPanelMetadata"
import { trimMiddle } from "@nova/util-strings"
import { deleteUpload, selectVideoEvents } from "../VideoSlice"
import { DeleteFlight } from "panels/content/side-panel/upload-flow/components/DeleteFlight"
import { UploadProgressBar } from "panels/content/side-panel/upload-flow/components/UploadProgressBar"
import { addUploadTimestamp } from "features/map/layers/slice"
import { selectUploadTimestampForFlightId } from "features/map/layers/selectors"
import { FlightUploadDropbox } from "panels/content/side-panel/upload-flow/components/FlightUploadDropbox"
import { get } from "nodash"
import { GenericDataUsage } from "panels/content/side-panel/upload-flow/components/GenericDataUsage"
import {
    flightExceededUsage,
    getSummaryFromUploads,
} from "panels/content/side-panel/upload-flow/module"
import { Layer } from "@nova/protobuf"
import { VideoPreviewFilesDemo } from "./VideoPreviewFilesDemo"
import { zoomToShowPoint } from "features/map/control/slice"
import { openVideoFlight } from "plugins/Video/state/thunks"

class ProgressEmitter extends EventEmitter {
    public progresses = {} as Record<string, number[]>
    setProgresses(flightId: string, p: number[]) {
        this.progresses[flightId] = p
        this.emit(flightId, p)
    }
}

const progressController = new ProgressEmitter()

export const useVideoProgress = (flightId: string) => {
    const [uploadProgress, setUploadProgress] = useState(
        progressController.progresses[flightId]?.[1] || 0
    )

    useEffect(() => {
        const listener = (p: number[]) => {
            setUploadProgress(p[1])
        }
        progressController.addListener(flightId, listener)
        return () => {
            progressController.removeListener(flightId, listener)
        }
    }, [flightId])

    return uploadProgress
}

export const VideoPreviewFiles = () => {
    const { uploads, job, flightId, useProcessing, selectFlightById, selectUploadSize } =
        useUploadFlowContext()

    const dispatch = useAppDispatch()

    const localVideoEvents = useAppSelector(selectVideoEvents(flightId))
    const flight = useAppSelector(selectFlightById(flightId)) as Layer
    const isDemo = !!flight?.demoId && !!job
    const uploadSize = useAppSelector(selectUploadSize(flightId))
    const uploadTimestamp = useAppSelector(selectUploadTimestampForFlightId(flightId))
    const uploadProgress = useVideoProgress(flightId)

    const { processCallback, dependencies } = useProcessing((p: number[]) => {
        progressController.setProgresses(flightId, p)
    })

    const summary = getSummaryFromUploads(uploads)
    const canUpload =
        !summary.stateCount?.[ImageProcessingState.PendingUpload] &&
        !summary.stateCount?.[ImageProcessingState.Uploading] &&
        !!summary.stateCount?.[ImageProcessingState.ProcessingDone]

    const isDone = job?.status === JobStatusType.COMPLETED

    const canView = useMemo(
        () =>
            uploads.every(
                (upload) => "state" in upload && upload.state >= ImageProcessingState.ProcessingDone
            ),
        [uploads]
    )
    const {
        loading: processInProgress,
        createJob,
        error,
    } = useAsyncCallback(
        {
            createJob: async () => {
                dispatch(
                    addUploadTimestamp({
                        flightId,
                        timestamp: new Date().toISOString(),
                    })
                )
                await processCallback()
            },
        },
        [...dependencies, flightId]
    )
    const processStarted = useMemo(() => {
        return !!(processInProgress || error || job || uploadProgress)
    }, [processInProgress, error, job, uploadProgress])

    const exceededUsage = flightExceededUsage[flightId]
    const hasExceededUploads = exceededUsage > 0

    const [videoFile, srtFile] = useMemo(() => {
        return [
            uploads.find((upload) => {
                const isSRT = get(upload, "isSRT", false)
                return !isSRT
            }),
            uploads.find((upload) => {
                const isSRT = get(upload, "isSRT", false)
                return isSRT
            }),
        ]
    }, [uploads])

    useEffect(() => {
        if (!localVideoEvents) return
        const [lng, lat] = localVideoEvents?.[0]
        dispatch(zoomToShowPoint({ lat: lat, lng: lng, zoomLevel: 14 }))
    }, [dispatch, localVideoEvents])

    const videoName = get(videoFile, "name") as string
    const srtName = get(srtFile, "name") as string

    if (isDemo) return <VideoPreviewFilesDemo />

    return (
        <>
            <div className="border-b border-gray-700" />
            {canUpload && !processStarted && !isDone && !hasExceededUploads && (
                <div className="my-2 flex flex-col gap-2">
                    <div className="flex justify-between">
                        <h5 className="text-gray-0">Ready to Upload</h5>
                        <h5 className="text-gray-100">{`${uploads.length} files`}</h5>
                    </div>
                    <div className="my-2 flex flex-col gap-1">
                        <FileItem
                            flightId={flightId}
                            name={videoName || "Video File Missing"}
                            id={videoFile?.id}
                            isError={!videoFile}
                        />
                        <FileItem
                            flightId={flightId}
                            name={srtName || "SRT File Missing"}
                            id={srtFile?.id}
                            isWarning={!srtFile}
                        />
                    </div>
                </div>
            )}
            {(!videoFile || !srtFile) && !processStarted && !isDone && !hasExceededUploads && (
                <FlightUploadDropbox isAddMoreFiles />
            )}
            {/* Upload Info*/}
            {processStarted && !isDone && !hasExceededUploads && (
                <UploadProgressBar
                    progress={uploadProgress}
                    total={uploadSize}
                    timeElapsed={uploadTimestamp}
                />
            )}
            {isDone && (
                <div className="flex items-center gap-1 my-4">
                    <Icons.Icon Icon={Icons.Checkmark} className="text-green-500" />
                    <h5 className="text-gray-0">Upload Complete</h5>
                </div>
            )}
            {hasExceededUploads && <GenericDataUsage remainingBytes={exceededUsage} />}
            <div className="mt-2">
                <InspectorPanelMetadata
                    entity={flight as SidePanelLayer}
                    uploads={uploads as GenericUpload[]}
                    uploadSize={uploadSize}
                    completedUploadCount={summary.stateCount[ImageProcessingState.UploadDone] || 0}
                    hideCreator
                    hideFileCount={0 > 0}
                    className=""
                    isSingleFile
                />
                <div className="border-b border-gray-700" />
                {/* action buttons */}
                {canView && (
                    <ButtonDynamic
                        isTertiary={(!processStarted && !isDone) || hasExceededUploads}
                        title={"Play Video"}
                        RightIcon={Icons.PlayVideo}
                        className="w-full !px-0 mt-2"
                        onClick={() => {
                            dispatch(openVideoFlight({ flightId }))
                        }}
                        disabled={!videoFile}
                    />
                )}
                <div className="flex gap-2 items-center mt-2">
                    <DeleteFlight />
                    {canUpload && !processStarted && !hasExceededUploads && (
                        <ButtonPrimary
                            title={"Upload"}
                            className="w-full !px-0"
                            onClick={createJob}
                            disabled={processStarted || !videoFile}
                        />
                    )}
                </div>
            </div>
        </>
    )
}

const FileItem = ({
    id,
    flightId,
    name,
    isError,
    isWarning,
}: {
    id?: string
    flightId: string
    name: string
    isError?: boolean
    isWarning?: boolean
}) => {
    const dispatch = useAppDispatch()
    return (
        <div className="flex gap-2 items-center justify-between">
            <div className="flex gap-2 items-center">
                {isError ? (
                    <Icons.SmallIcon Icon={Icons.Error} className="text-red-500" />
                ) : isWarning ? (
                    <Icons.SmallIcon Icon={Icons.Error} className="text-orange-500" />
                ) : (
                    <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />
                )}
                <small className="text-gray-100 normalLineHeight">
                    {trimMiddle(name, { maxLength: 25 })}
                </small>
            </div>
            {id && (
                <Icons.SmallIcon
                    Icon={Icons.Trash}
                    className="text-gray-300 hover:text-red-500 cursor-pointer"
                    onClick={() => dispatch(deleteUpload({ uploadId: id, setId: flightId }))}
                />
            )}
        </div>
    )
}
